if (!customElements.get('localization-form')) {
  customElements.define(
    'localization-form',
    class LocalizationForm extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          input: this.querySelector('input[name="locale_code"], input[name="country_code"]'),
          button: this.querySelector('button'),
          panel: this.querySelector('.disclosure__list-wrapper'),
        };
        this.marketSelector = this.querySelector('[data-maison-market-selector]');
        this.search = this.querySelector('[data-maison-market-search]');
        this.count = this.querySelector('[data-maison-market-count]');
        this.empty = this.querySelector('[data-maison-market-empty]');
        this.items = Array.from(this.querySelectorAll('[data-maison-market-item]'));
        this.closeTimer = null;

        if (!this.elements.button || !this.elements.panel) return;

        this.elements.button.addEventListener('click', this.openSelector.bind(this));
        this.elements.button.addEventListener('focusout', this.closeSelector.bind(this));
        this.boundContainerKeyUp = this.onContainerKeyUp.bind(this);
        this.boundContainerKeyDown = this.onContainerKeyDown.bind(this);
        this.documentKeyboardConnected = false;
        if (!this.isMarketSelector()) {
          this.addEventListener('keyup', this.boundContainerKeyUp);
          this.addEventListener('keydown', this.boundContainerKeyDown);
        }

        this.querySelectorAll('a[data-value]').forEach((item) =>
          item.addEventListener('click', this.onItemClick.bind(this))
        );
        this.querySelectorAll('[data-maison-market-close]').forEach((control) =>
          control.addEventListener('click', () => this.hidePanel(true))
        );
        if (this.search) this.search.addEventListener('input', this.filterCountries.bind(this));
      }

      connectedCallback() {
        if (!this.isMarketSelector() || !this.elements.panel || !document.body) return;
        if (!this.documentKeyboardConnected) {
          document.addEventListener('keyup', this.boundContainerKeyUp);
          document.addEventListener('keydown', this.boundContainerKeyDown);
          this.documentKeyboardConnected = true;
        }
        if (this.elements.panel.parentElement === document.body) return;
        this.elements.panel.setAttribute('data-maison-market-portal', '');
        document.body.appendChild(this.elements.panel);
      }

      disconnectedCallback() {
        window.setTimeout(() => {
          if (this.isConnected || !this.isMarketSelector() || !this.elements.panel) return;
          if (this.documentKeyboardConnected) {
            document.removeEventListener('keyup', this.boundContainerKeyUp);
            document.removeEventListener('keydown', this.boundContainerKeyDown);
            this.documentKeyboardConnected = false;
          }
          window.clearTimeout(this.closeTimer);
          this.elements.panel.classList.remove('is-open');
          this.elements.panel.setAttribute('hidden', true);
          if (this.elements.panel.parentElement === document.body) this.elements.panel.remove();
          if (document.body && !document.querySelector('.maison-market-layer.is-open')) {
            this.unlockDocumentScroll();
          }
        }, 0);
      }

      isMarketSelector() {
        return Boolean(this.marketSelector && this.search);
      }

      lockDocumentScroll() {
        if (!document.body || document.body.classList.contains('maison-market-open')) return;
        const scrollY = window.scrollY;
        document.body.dataset.maisonMarketScrollY = String(scrollY);
        document.body.style.setProperty('--maison-market-scroll-offset', `${-scrollY}px`);
        document.body.classList.add('maison-market-open');
      }

      unlockDocumentScroll() {
        if (!document.body || document.querySelector('.maison-market-layer.is-open')) return;
        const scrollY = Number(document.body.dataset.maisonMarketScrollY || 0);
        document.body.classList.remove('maison-market-open');
        document.body.style.removeProperty('--maison-market-scroll-offset');
        delete document.body.dataset.maisonMarketScrollY;

        const root = document.documentElement;
        const previousScrollBehavior = root.style.scrollBehavior;
        root.style.scrollBehavior = 'auto';
        window.scrollTo(0, scrollY);
        window.requestAnimationFrame(() => {
          root.style.scrollBehavior = previousScrollBehavior;
        });
      }

      hidePanel(restoreFocus = false) {
        if (!this.elements.button || !this.elements.panel) return;
        window.clearTimeout(this.closeTimer);
        this.elements.button.setAttribute('aria-expanded', 'false');
        this.elements.panel.classList.remove('is-open');

        const finish = () => {
          this.elements.panel.setAttribute('hidden', true);
          if (!document.querySelector('.maison-market-layer.is-open')) {
            this.unlockDocumentScroll();
          }
          if (restoreFocus) this.elements.button.focus({ preventScroll: true });
        };

        if (this.isMarketSelector() && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          this.closeTimer = window.setTimeout(finish, 760);
        } else {
          finish();
        }
      }

      onContainerKeyUp(event) {
        if (event.code.toUpperCase() !== 'ESCAPE') return;
        if (this.elements.button.getAttribute('aria-expanded') === 'false') return;
        event.preventDefault();
        event.stopPropagation();
        this.hidePanel(true);
      }

      onContainerKeyDown(event) {
        if (event.key !== 'Tab' || !this.isMarketSelector() || this.elements.panel.hidden) return;
        const focusable = Array.from(
          this.elements.panel.querySelectorAll('button:not([disabled]), input:not([disabled]), a[href]:not([hidden])')
        ).filter((element) => element.offsetParent !== null);
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }

      onItemClick(event) {
        event.preventDefault();
        const form = this.querySelector('form');
        this.elements.input.value = event.currentTarget.dataset.value;
        if (form) form.submit();
      }

      openSelector() {
        const isOpen = this.elements.button.getAttribute('aria-expanded') === 'true';
        if (isOpen) {
          this.hidePanel(true);
          return;
        }

        document.querySelectorAll('localization-form').forEach((form) => {
          if (form !== this && typeof form.hidePanel === 'function') form.hidePanel(false);
        });

        window.clearTimeout(this.closeTimer);
        this.elements.panel.removeAttribute('hidden');
        this.elements.button.setAttribute('aria-expanded', 'true');

        if (this.isMarketSelector()) {
          this.lockDocumentScroll();
          window.requestAnimationFrame(() => {
            this.elements.panel.classList.add('is-open');
            if (!window.matchMedia('(pointer: coarse)').matches) {
              window.requestAnimationFrame(() => this.search.focus({ preventScroll: true }));
            }
          });
        } else {
          this.elements.button.focus();
        }
      }

      closeSelector(event) {
        const next = event.relatedTarget;
        const isChild = next && (this.elements.panel.contains(next) || this.elements.button.contains(next));
        if (!next || !isChild) this.hidePanel(false);
      }

      filterCountries() {
        const query = this.search.value.trim().toLocaleLowerCase();
        let visible = 0;
        this.items.forEach((item) => {
          const match = (item.dataset.search || '').toLocaleLowerCase().includes(query);
          item.hidden = !match;
          if (match) visible += 1;
        });
        this.elements.panel.querySelectorAll('.maison-locale-divider').forEach((divider) => {
          divider.hidden = Boolean(query);
        });
        if (this.empty) this.empty.hidden = visible !== 0;
        if (this.count) this.count.textContent = `${visible} ${visible === 1 ? 'OPTION' : 'OPTIONS'}`;
      }
    }
  );
}
